/* Derives the canonical ForecastSnapshot ("actual") for each processed email
   from existing simulation data (spec §8). Pure and deterministic: replays the
   accumulation (participants, memo, document register, trackers) across the
   processed entries. Live runs carry structured check results; pre-baked
   replays expose a subset (routing, participants, memo facts, documents,
   approvals) and leave the rest empty. */

import { accumulateDocumentRegister, buildAddressRoster, synthParticipantResult } from "../utils";
import type {
  Approval, CheckMap, ProcessedEntry, SimCheck, WorkstreamLite,
} from "../types";
import {
  emptySnapshot,
  type ForecastMappedParticipant,
  type ForecastSnapshot,
  type ForecastTrackers,
} from "./types";

const TRACKER_CHECKS: Array<{ checkId: string; key: keyof ForecastTrackers; resultKey: string; matchOn: string[] }> = [
  { checkId: "timeline", key: "timeline", resultKey: "entries", matchOn: ["date", "event"] },
  { checkId: "issues", key: "issues", resultKey: "issues", matchOn: ["issue"] },
  { checkId: "closing", key: "closingItems", resultKey: "items", matchOn: ["condition"] },
  { checkId: "signature", key: "signatures", resultKey: "signatures", matchOn: ["document", "party"] },
  { checkId: "diligence", key: "diligence", resultKey: "requests", matchOn: ["item"] },
  { checkId: "commitments", key: "commitments", resultKey: "commitments", matchOn: ["who", "what"] },
  { checkId: "fundsflow", key: "wires", resultKey: "wires", matchOn: ["from", "to"] },
];

type Entry = Record<string, unknown>;

const normKey = (e: Entry, fields: string[]): string =>
  fields.map((f) => String(e[f] ?? "").trim().toLowerCase().replace(/\s+/g, " ")).join("|");

/* Update-else-append merge, mirroring the server's mergeTracker (history is
   not tracked here — only the current entry state matters for comparison). */
function mergeTracker(prior: Entry[], emitted: unknown, matchOn: string[]): Entry[] {
  const out = prior.map((e) => ({ ...e }));
  if (!Array.isArray(emitted)) return out;
  const index = new Map<string, number>(out.map((e, i) => [normKey(e, matchOn), i] as [string, number]));
  for (const raw of emitted) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const e = raw as Entry;
    const k = normKey(e, matchOn);
    if (!k.replace(/\|/g, "").trim()) continue;
    const at = index.get(k);
    if (at === undefined) {
      index.set(k, out.length);
      out.push({ ...e });
    } else {
      const patch: Entry = {};
      for (const f of Object.keys(e)) if (e[f] != null) patch[f] = e[f];
      out[at] = { ...out[at], ...patch };
    }
  }
  return out;
}

const str = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
};

function findCheck(checks: SimCheck[], id: string): SimCheck | undefined {
  return checks.find((c) => c.id === id && !c.skipped);
}

function result(checks: SimCheck[], id: string): Record<string, unknown> | null {
  const c = findCheck(checks, id);
  return (c && c.result) || null;
}

function fired(
  checks: SimCheck[],
  id: string,
  thresholds: Record<string, number>,
  CM: CheckMap,
): boolean {
  const c = findCheck(checks, id);
  if (!c) return false;
  const tau = thresholds[id] ?? CM[id]?.tau ?? 0.6;
  return c.conf >= tau;
}

export interface ExtractInputs {
  processed: ProcessedEntry[];
  approvals: Approval[];
  mattersInScope: WorkstreamLite[];
  wsNameMap: Record<string, string>;
  thresholds: Record<string, number>;
  CM: CheckMap;
}

export function extractActualSnapshots(inputs: ExtractInputs): ForecastSnapshot[] {
  const { processed, approvals, mattersInScope, wsNameMap, thresholds, CM } = inputs;
  const roster = buildAddressRoster(mattersInScope);
  const entityNames: Record<string, string> = {};
  const personByEmail: Record<string, string> = {};
  mattersInScope.forEach((w) =>
    (w.entities || []).forEach((en) => {
      entityNames[en.id] = en.name;
      (en.people || []).forEach((p) => {
        if (p.email) personByEmail[p.email.toLowerCase()] = p.name;
      });
    }),
  );

  /* accumulated state */
  const mapped = new Map<string, ForecastMappedParticipant>();
  const unknown = new Set<string>();
  const memo: Record<string, { phase: string | null; fields: Record<string, { value: string; source: string | null }> }> = {};
  mattersInScope.forEach((w) => {
    memo[w.id] = { phase: w.phase || "Setup", fields: {} };
  });
  const trackers: Record<string, Record<string, Entry[]>> = {};

  const out: ForecastSnapshot[] = [];

  processed.forEach((p, i) => {
    const snap = emptySnapshot(p.email.id);
    const checks = p.checks;
    const cat = p.R.catalogs[0] || ["unassoc", 0];
    const wsId = cat[0];
    const routed = wsId !== "unassoc";

    snap.routing = {
      workstreamId: routed ? wsId : null,
      workstreamName: routed ? wsNameMap[wsId] || wsId : null,
      confidence: cat[1] ?? 0,
      routed,
    };

    /* participants — accumulated */
    const pres = result(checks, "participant") as {
      mapped?: Array<{ email?: string; entityId?: string; personId?: string; role?: string }>;
      unknown?: string[];
    } | null;
    const part = pres || synthParticipantResult(p.email, roster);
    if (part) {
      (part.mapped || []).forEach((m) => {
        const email = String(m.email || "").toLowerCase();
        if (!email) return;
        unknown.delete(email);
        mapped.set(email, {
          email,
          entity: m.entityId ? entityNames[m.entityId] || m.entityId : null,
          person: personByEmail[email] || null,
          role: str(m.role),
          confirmed: true,
        });
      });
      (part.unknown || []).forEach((u) => {
        const email = String(u || "").toLowerCase();
        if (email && !mapped.has(email)) unknown.add(email);
      });
    }
    snap.participants = {
      mapped: [...mapped.values()].map((m) => ({ ...m })),
      unknown: [...unknown],
    };

    /* memo — accumulated; facts apply to the routed workstream */
    if (routed) {
      const ws = (memo[wsId] = memo[wsId] || { phase: null, fields: {} });
      (p.R.facts || []).forEach((f) => {
        const [field, value, , source] = f;
        if (field && value) ws.fields[field] = { value, source: str(source) ?? `Email #${p.email.id}` };
      });
    }

    /* phase flag + memo phase update */
    const phaseRes = result(checks, "phase") as { newPhase?: string } | null;
    let newPhase = str(phaseRes?.newPhase);
    if (!newPhase) {
      const pa = approvals.find((a) => a.emailId === p.email.id && a.type === "phase");
      const m = pa?.label.match(/→\s*(.+)$/);
      if (m) newPhase = m[1].trim();
    }
    if (newPhase && fired(checks, "phase", thresholds, CM) && routed) {
      memo[wsId].phase = newPhase;
    }

    snap.memo = JSON.parse(JSON.stringify(memo));

    /* document register — accumulated from attachments on routed emails */
    const reg = accumulateDocumentRegister(processed.slice(0, i + 1));
    Object.keys(reg).forEach((ws) => {
      if (ws === "unassoc") return;
      snap.documentRegister[ws] = reg[ws].map((d) => ({
        docType: d.doc_type,
        name: d.name,
        currentVersion: d.current_version,
      }));
    });

    /* doc funnel — per-email, from the structured doc_register result. The
       register agent owns both triage (noise/substantive/updated_version) and
       relevance (anything not classified "noise" bears on the deal). */
    const reg2 = result(checks, "doc_register") as {
      documents?: Array<{ filename?: string; classification?: string }>;
    } | null;
    (reg2?.documents || []).forEach((d) => {
      if (!d.filename || !d.classification) return;
      snap.docFunnel.triage.push({
        filename: d.filename,
        classification: d.classification as "updated" | "substantive" | "noise",
      });
      snap.docFunnel.relevance.push({
        filename: d.filename,
        relevant: d.classification !== "noise",
      });
    });
    const delta = result(checks, "doc_delta") as {
      changes?: Array<{ clause?: string; before?: string; after?: string; severity?: string }>;
      maxSeverity?: string;
    } | null;
    (delta?.changes || []).forEach((c) => {
      if (c.clause)
        snap.docFunnel.delta.push({
          clause: c.clause,
          before: str(c.before),
          after: str(c.after),
          severity: (c.severity as "High" | "Medium" | "Low") || "Low",
        });
    });
    if (delta?.maxSeverity && ["High", "Medium", "Low"].includes(delta.maxSeverity))
      snap.docFunnel.maxSeverity = delta.maxSeverity as "High" | "Medium" | "Low";

    /* flags — per-email */
    const anom = result(checks, "anomaly") as { anomaly?: boolean; kind?: string } | null;
    const anomDetected = anom ? !!anom.anomaly : fired(checks, "anomaly", thresholds, CM);
    snap.flags.anomaly = { detected: anomDetected, kind: anomDetected ? str(anom?.kind) : null };
    const priv = result(checks, "privilege") as { privileged?: boolean; waiverRisk?: string } | null;
    const privileged = priv ? !!priv.privileged : fired(checks, "privilege", thresholds, CM);
    snap.flags.privilege = { privileged, waiverRisk: privileged ? str(priv?.waiverRisk) : null };
    snap.flags.phase = { newPhase };
    const np = result(checks, "newpart") as {
      proposals?: Array<{ email?: string; entityName?: string; kind?: string; role?: string }>;
    } | null;
    (np?.proposals || []).forEach((x) => {
      if (x.email)
        snap.flags.newParticipants.push({
          email: String(x.email).toLowerCase(),
          entityName: str(x.entityName),
          kind: str(x.kind),
          role: str(x.role),
        });
    });
    const conf = result(checks, "conflict") as {
      conflicts?: Array<{ field?: string; oldValue?: string; newValue?: string; resolution?: string }>;
    } | null;
    if (conf?.conflicts) {
      conf.conflicts.forEach((c) => {
        if (c.field)
          snap.flags.conflicts.push({
            field: c.field,
            oldValue: str(c.oldValue) ?? "",
            newValue: str(c.newValue) ?? "",
            resolution: (c.resolution as "supersede" | "conflict" | "restate") || "conflict",
          });
      });
    } else {
      approvals
        .filter((a) => a.emailId === p.email.id && a.type === "conflict" && a.options?.field)
        .forEach((a) =>
          snap.flags.conflicts.push({
            field: a.options.field,
            oldValue: str(a.options.options?.[0]) ?? "",
            newValue: str(a.options.options?.[1]) ?? "",
            resolution: "conflict",
          }),
        );
    }

    /* escalation — per-email */
    const sa = result(checks, "senior_associate") as { recommendation?: string; urgency?: string } | null;
    const escalated = fired(checks, "senior_associate", thresholds, CM);
    snap.escalation = {
      triggered: escalated,
      recommendation: escalated ? str(sa?.recommendation) : null,
      urgency: escalated ? ((str(sa?.urgency)?.toLowerCase() as "high" | "medium" | "low") ?? null) : null,
    };

    /* trackers — accumulated per workstream from structured agent results */
    if (routed) {
      for (const tk of TRACKER_CHECKS) {
        const r = result(checks, tk.checkId);
        const emitted = r?.[tk.resultKey];
        if (!Array.isArray(emitted) || emitted.length === 0) continue;
        const ws = (trackers[wsId] = trackers[wsId] || {});
        ws[tk.key] = mergeTracker(ws[tk.key] || [], emitted, tk.matchOn);
      }
    }
    Object.keys(trackers).forEach((ws) => {
      const t: ForecastTrackers = {
        timeline: [], issues: [], commitments: [], diligence: [],
        wires: [], signatures: [], closingItems: [],
      };
      for (const tk of TRACKER_CHECKS) {
        (t[tk.key] as Entry[]) = (trackers[ws][tk.key] || []).map((e) => ({ ...e }));
      }
      snap.trackers[ws] = t;
    });

    /* cross-checks — per-email */
    const disp = result(checks, "dispatch") as {
      actions?: Array<{ document?: string; action?: string; owner?: string; reason?: string }>;
    } | null;
    (disp?.actions || []).forEach((a) => {
      if (a.document && a.action)
        snap.crossChecks.dispatch.push({
          document: a.document, action: a.action, owner: str(a.owner), reason: str(a.reason),
        });
    });
    const dc = result(checks, "docchange") as {
      edits?: Array<{ document?: string; section?: string; change?: string; basis?: string }>;
    } | null;
    (dc?.edits || []).forEach((e) => {
      if (e.document && e.change)
        snap.crossChecks.docChanges.push({
          document: e.document, section: str(e.section), change: e.change, basis: str(e.basis),
        });
    });
    const fid = result(checks, "fidelity") as {
      mismatches?: Array<{ field?: string; memoValue?: string; docValue?: string; document?: string }>;
    } | null;
    (fid?.mismatches || []).forEach((m) => {
      if (m.field)
        snap.crossChecks.fidelity.push({
          field: m.field,
          memoValue: str(m.memoValue) ?? "",
          docValue: str(m.docValue) ?? "",
          document: str(m.document) ?? "",
        });
    });
    const disc = result(checks, "discrepancy") as {
      discrepancies?: Array<{ field?: string; memoValue?: string; statedValue?: string; speaker?: string; severity?: string }>;
    } | null;
    (disc?.discrepancies || []).forEach((d) => {
      if (d.field)
        snap.crossChecks.discrepancies.push({
          field: d.field,
          memoValue: str(d.memoValue) ?? "",
          statedValue: str(d.statedValue) ?? "",
          speaker: str(d.speaker),
          severity: str(d.severity) ?? "medium",
        });
    });

    /* state of play — per-email */
    const sop = result(checks, "stateofplay") as {
      phase?: string; momentum?: string; tracking?: string; briefing?: string;
      nextActions?: Array<{ action?: string; who?: string }>;
    } | null;
    if (sop) {
      snap.stateOfPlay = {
        phase: str(sop.phase),
        momentum: (str(sop.momentum)?.toLowerCase() as ForecastSnapshot["stateOfPlay"]["momentum"]) ?? null,
        tracking: (str(sop.tracking)?.toLowerCase() as ForecastSnapshot["stateOfPlay"]["tracking"]) ?? null,
        briefing: str(sop.briefing),
        nextActions: (sop.nextActions || [])
          .filter((a) => a.action)
          .map((a) => ({ action: String(a.action), who: str(a.who) })),
      };
    }

    /* approvals — per-email */
    snap.approvals = approvals
      .filter((a) => a.emailId === p.email.id)
      .map((a) => ({
        type: String(a.type || "").trim().toLowerCase().replace(/[\s-]+/g, "_"),
        severity: a.severity || (a.material ? "high" : "medium"),
        detail: str(a.detail),
      }));

    out.push(snap);
  });

  return out;
}
